// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Divider, Button, Grid, Icon, Paper, Stepper, StepButton, Step, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import _ from 'lodash';

// Internal Dependencies
import Colours from '../../../styles/colours';
import Mechanics from './mechanics';
import Preview from './preview';
import TermsSource from './termsSource';
import OverLoading from '../../global/OverLoading';
import Photos from './photos';
import Media from './media';
import Availability from './availability';
import PartnersTags from './partnersTags';
import apiService from '../../../services.js/apiService';

import { getPage } from '../../../actions/admin/pageAction';
import { getCategories } from '../../../actions/admin/category';
import Constants, { Status } from '../../../common/constants';
import { IsEmpty, IsValidWwwAndHttpUrl } from '../../../common/checks';
import { showDateInSGTime } from '../../../common/util';
import { handleError } from '../../../common/helpers';
import SupportIdAlert from '../../common/SupportIdAlert';

const useStyles = makeStyles(() => ({
  root: {
    width:      '100%',
    padding:    15,
    paddingTop: 20,
  },
}));

// eslint-disable-next-line no-shadow
const Post = ({ match, pageId, getPage, getCategories, onRedirect }) => {
  const stepperClasses = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed] = React.useState({});
  const steps = ['Media', 'Promotion Mechanics', 'Availability', 'T&Cs and Source', 'Partnerships, Tags and Post Date', 'Review'];

  const [postStatus, setPostStatus] = useState('');
  const [pageName, setPageName] = useState('');
  const [postId, setPostId] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [mallHighlight, setMallHighlight] = useState(false);
  const [ignoreHttpsCheck, setIgnoreHttpsCheck] = useState(false);
  const [description, setDescription] = useState('');
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoType, setPromoType] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [terms, setTerms] = useState('');
  const [isAvailOnline, setAvailOnline] = useState(false);
  const [promoIsNationwide, setPromoIsNationwide] = useState(false);
  const [promoAvailOnlineAt, setPromoAvailOnlineAt] = useState('');
  const [promoLocations, setPromoLocations] = useState([]);
  const [promoUrl, setPromoUrl] = useState('');
  const [promoPartners, setPromoPartners] = useState([]);
  const [promoSubsidiaries, setPromoSubsidiaries] = useState([]);
  const [promoCreditCards, setPromoCreditCards] = useState([]);
  const [userTags, setUserTags] = useState([]);
  const [pageType, setPageType] = useState('');
  const [promoCategories, setPromoCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mealsAvailNA, setMealsAvailNA] = useState(false);
  const [partnerBuildings, setPartnerBuildings] = useState([]);
  const [showPicMarker, setShowPicMarker] = useState(false);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const [requestId, setRequestId] = useState(null);

  async function fetchPostData() {
    try {
      setIsLoading(true);

      const post = await apiService.getPostById(match.params.postId);
      if (post) {
        setPostId(post.id);
        setPageType(post.pageType ? post.pageType : '');

        setPostStatus(post.status);
        setPageName(post.brand ? post.brand : '');
        setPostTitle(post.title ? post.title : '');
        setDescription(post.description ? post.description : '');
        setPromoCodes(post.promoCodes ? post.promoCodes : []);
        setPromoType(post.promoType ? post.promoType : []);

        setMallHighlight(post.isMallHighlight ? post.isMallHighlight : false);

        setDateFrom(post.promoFromDate ? showDateInSGTime(post.promoFromDate) : '');
        setDateTo(post.promoTillDate ? showDateInSGTime(post.promoTillDate) : '');

        setTerms(post.promoTerms
          ? post.promoTerms
          : '*Terms & conditions apply. Information is correct at the time of posting. Please check original source for details.');
        setPostImages(post.images);
        setVideos(post.videos);
        setPromoSubsidiaries(post.promoSubsidiaries ? post.promoSubsidiaries : []);
        setPromoCreditCards(post.promoCreditCards ? post.promoCreditCards : []);
        setPromoCategories(post.categories ? post.categories : []);
        setPromoAvailOnlineAt(post.promoAvailOnlineAt ? post.promoAvailOnlineAt : '');
        setPromoLocations(post.promoLocations ? post.promoLocations : []);
        setAvailOnline(post.isAvailOnline);
        setPromoIsNationwide(post.promoIsNationwide);
        setPromoPartners(post.promoPartners);
        setPromoUrl(post.promoUrl);
        setShowPicMarker(post.showPicMarker || false);

        const partnerBuildingsData = await apiService.getPartnerBuildings(post.pageId);
        setPartnerBuildings(partnerBuildingsData);

        const filteredMealsAvailableTags = post.mealsAvailable.filter((tag) => tag !== Constants.NotApplicable);
        const combinedTags = _.uniqBy(post.userTags.concat(filteredMealsAvailableTags), (tag) => tag);
        setUserTags(combinedTags);

        if (post.mealsAvailable.length === 1 && post.mealsAvailable[0] === Constants.NotApplicable) {
          setMealsAvailNA(true);
        }
      } else {
        onRedirect();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPostData();
    getPage(pageId).then((p) => setPage(p));
    getCategories().then((c) => setCategories(c));
  }, []);

  const getPreviewCom = () => {
    const config = { year: 'numeric', month: 'long', day: 'numeric' };
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);

    let from; const
      to = toDate.toLocaleDateString('en-GB', config);
    const fromDD = fromDate.getDate();
    const fromMM = fromDate.getMonth();
    const toMM = toDate.getMonth();
    const fromYY = fromDate.getFullYear();
    const toYY = toDate.getFullYear();

    if (fromMM === toMM && fromYY === toYY) {
      from = fromDD;
    } else if (fromMM !== toMM && fromYY === toYY) {
      from = fromDD.toString().concat(' ', months[fromMM]);
    } else {
      from = fromDate.toLocaleDateString('en-GB', config);
    }

    return (
      <Preview
        brand={pageName}
        title={postTitle}
        dateFrom={(dateFrom) ? String(from) : ''}
        dateTo={(dateTo) ? String(to) : ''}
        description={description}
        images={postImages}
        videos={videos}
        terms={terms}
        source={promoUrl}
      />
    );
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <>
            <Media
              postId={postId}
              videos={videos}
              onVideoSort={(d) => setVideos(d)}
              onVideoChanged={(e) => setVideos(e)}
            />
            <Divider
              style={{ marginTop: 15, marginBottom: 15 }}
            />
            <Photos
              match={match}
              images={postImages}
              onSetPostImages={(images) => { setPostImages(images); }}
              onSort={(images) => { setPostImages(images); }}
              showSaveImgPosBtn={false}
            />
          </>
        );
      case 1:
        return (
          <Mechanics
            postStatus={postStatus}
            promoCategories={promoCategories}
            categories={categories}
            title={postTitle}
            dateTo={dateTo}
            dateFrom={dateFrom}
            description={description}
            promoCodes={promoCodes}
            promoType={promoType}
            mallHighlight={mallHighlight}
            ignoreHttpsCheck={ignoreHttpsCheck}
            pageType={pageType}
            showPicMarker={showPicMarker}
            onTitleChanged={(e) => setPostTitle(e)}
            onDescriptionChanged={(e) => setDescription(e)}
            onDateFromChanged={(e) => setDateFrom(e)}
            onDateToChanged={(e) => setDateTo(e)}
            onPromoCategoriesChanged={(e) => setPromoCategories(e)}
            onPromoCodesChanged={(e) => setPromoCodes(e)}
            onPromoTypeChanged={(e) => setPromoType(e)}
            onMallHighlightChanged={(e) => setMallHighlight(e)}
            onIgnoreHttpsCheckChanged={(e) => setIgnoreHttpsCheck(e)}
            onShowPicMarkerChanged={(e) => setShowPicMarker(e)}
          />
        );
      case 2:
        return (
          <Availability
            postId={postId}
            page={page}
            promoAvailOnlineAt={promoAvailOnlineAt}
            promoLocations={promoLocations}
            partnerBuildings={partnerBuildings}
            isAvailOnline={isAvailOnline}
            onPromoLocationsChanged={(e) => setPromoLocations(e)}
            onPromoAvailOnlineAtChanged={(e) => setPromoAvailOnlineAt(e)}
            onPromoIsNationwideChanged={(e) => setPromoIsNationwide(e)}
            onAvailOnlineChanged={(e) => setAvailOnline(e)}
          />
        );
      case 3:
        return (
          <TermsSource
            terms={terms}
            source={promoUrl}
            onTermsChanged={(e) => setTerms(e)}
            onSourceChanged={(e) => setPromoUrl(e)}
          />
        );
      case 4:
        return (
          <PartnersTags
            postId={match.params.postId}
            page={page}
            postStatus={postStatus}
            userTags={userTags}
            mealsAvailFlag={mealsAvailNA}
            promoPartners={promoPartners}
            promoCreditCards={promoCreditCards}
            promoSubsidiaries={promoSubsidiaries}
            onUserTagsChanged={(e) => setUserTags(e)}
            onMealsAvailableChanged={(e) => setMealsAvailNA(e)}
            onPromoPartnersChanged={(e) => setPromoPartners(e)}
            onPromoCreditCardsChanged={(e) => setPromoCreditCards(e)}
            onPromoSubsidiariesChanged={(e) => setPromoSubsidiaries(e)}
          />
        );
      case 5:
        return getPreviewCom();
      default:
        return <Grid>hello</Grid>;
    }
  }

  const handleUpdate = async (e, type) => {
    setIsLoading(true);
    e.preventDefault();
    const hasEmptyDisplay = postImages.some((val) => IsEmpty(val.display));

    if (hasEmptyDisplay) {
      toast.error('Please crop all the image or delete uncropped image before do the save');
      return;
    }
    setUpdating(true);
    let status;
    if (type === Constants.Status.Live) {
      status = 'Live';

      // Check description URL must be https when updating post to Live
      if (!ignoreHttpsCheck) {
        const listDescription = description.split(' ');
        for (let index = 0; index < listDescription.length; index += 1) {
          if (IsValidWwwAndHttpUrl(listDescription[index])) {
            toast.error('Please check the Promotion Description, all URLs should use the https');
            setUpdating(false);
            return;
          }
        }
      }
    } else if (type === Constants.Status.Draft) {
      status = 'Draft';
      setOpenModal(false);
    } else {
      toast.error('Status is invalid. Pls inform IT on this issue.');
      return;
    }
    try {
      const categoryIds = promoCategories.map((category) => category.id);

      // Standardize to pass Singapore timezone to the server
      const startDate = dateFrom ? new Date(dateFrom) : null;
      const promoStartDate = startDate ? new Date(`${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()} 00:00:00 +8:00`) : null;

      const endDate = dateTo ? new Date(dateTo) : null;
      const promoEndDate = endDate ? new Date(`${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()} 23:59:59 +8:00`) : null;

      // user tags & meals available
      const mealTagsOnly = _.compact(_.map(userTags, (tag) => (Constants.MealTags.includes(tag) ? tag : null))) || [];
      const mealsAvailable =  mealTagsOnly.filter((tag) => tag !== Constants.NotApplicable);

      // updating status...
      if (page.status === Constants.PageStatus.Pending && status === Status.Live) {
        status = Status.Pending;
      }

      /**
       * Need to assert pageId to payload since we need to check on middleware
       * whether page is official or not
       * */
      const payload = {
        title:              postTitle,
        description,
        status,
        images:             postImages,
        videos,
        categories:         categoryIds,
        isMallHighlight:    mallHighlight,
        promoCodes,
        promoType,
        promoTerms:         terms,
        promoFromDate:      promoStartDate,
        promoTillDate:      promoEndDate,
        promoAvailOnlineAt: promoAvailOnlineAt ? String(promoAvailOnlineAt).trim() : '',
        promoLocations,
        isAvailOnline,
        promoIsNationwide,
        promoPartners,
        promoSubsidiaries,
        promoCreditCards,
        promoUrl:           promoUrl ? String(promoUrl).trim() : '',
        userTags,
        mealsAvailable:     mealsAvailable.length > 0
          ? mealsAvailable
          : IsEmpty(mealsAvailable) && mealsAvailNA === false
            ? []
            : [Constants.NotApplicable],
        ignoreHttpsCheck,
        pageId: parseInt(pageId, 10),
        showPicMarker,
      };

      if (![Constants.PageType.FNB, Constants.PageType.Retailer, Constants.PageType.ECommerce].includes(page.type)) {
        delete payload.mealsAvailable;
      } else if (
        type === Constants.Status.Live
        && [Constants.PageType.FNB, Constants.PageType.Retailer, Constants.PageType.ECommerce].includes(page.type)
        && IsEmpty(mealsAvailable)
        && mealsAvailNA === false
      ) {
        toast.error('Please select if promo is available for Breakfast, Lunch, Tea, Dinner, Supper or N.A');
        setUpdating(false);
        return;
      }

      // eslint-disable-next-line react/prop-types
      await apiService.updatePost(match.params.postId, payload);
      setRequestId(null);
      await fetchPostData();
      toast.success('Post updated');
    } catch (err) {
      if (!IsEmpty(err?.requestId)) {
        setRequestId(err.requestId);
      } else {
        setRequestId(null);
      }

      handleError(
        err,
        'Error in Post.promo.promo.handleUpdate()',
        { additionalData: { postId: match.params.postId } },
      );
    } finally {
      setUpdating(false);
      setIsLoading(false);
    }
  };

  /* Overlay Scrollbar on Div instead of Sharing (Avoid Screen Squeeze) */
  const element = document.querySelector('div');
  element.style.setProperty('overflow-y', 'overlay');

  if (isLoading && IsEmpty(postId)) {
    return <OverLoading />;
  }

  return (
    <>
      {isLoading && <OverLoading />}
      <div className="row">
        <div className="col-sm-12">
          <div className="content-header" style={{ backgroundColor: Colours.White, borderBottom: 'none' }}>
            <Grid item container direction="row" alignItems="center" xs={12} style={{ minHeight: 50 }}>
              <Grid
                item
                xs={2}
                md={1}
                container
                direction="row"
                alignItems="center"
                justify="center"
              >
                <Grid item container justify="flex-start">
                  <Typography variant="h1" className="d-flex align-items-center">
                    <div onClick={() => onRedirect()} className="d-flex align-items-center" aria-hidden="true">
                      <Icon className="back">arrow_back</Icon>
                    </div>
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} md container direction="row">
                <Stepper classes={{ root: stepperClasses.root }} nonLinear activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepButton
                        completed={completed[index]}
                        onClick={() => {
                          const hasEmptyDisplay = postImages.some((val) => IsEmpty(val.display));

                          if (hasEmptyDisplay) {
                            toast.error('Please crop all the image first before move to another step');
                          } else {
                            setActiveStep(index);
                          }
                        }}
                      >
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>
          </div>
        </div>
        {
          (activeStep !== steps.length - 1)
            ? (
              <>
                <div className="col-lg-12 row">
                  <div className="col-xs-12 col-md-0 col-lg-1" />
                  <div className="col-xs-12 col-md-12 col-lg-7">
                    {getStepContent(activeStep)}
                    <div className="d-flex" style={{ justifyContent: 'flex-end', marginTop: 30 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          const hasEmptyDisplay = postImages.some((val) => IsEmpty(val.display));

                          if (hasEmptyDisplay) {
                            toast.error('Please crop all the image first before move to next step');
                          } else {
                            setActiveStep(activeStep + 1);
                          }
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                  <Paper className="d-none d-lg-block d-xl-block col-lg-3 col-lg-block" style={{ minHeight: '30vw', backgroundColor: Colours.LighGrayBkgrd, padding: 0, marginLeft: 20 }}>
                    {getPreviewCom()}
                  </Paper>
                </div>

                {
                  (postStatus === Constants.Status.Pending || postStatus === Constants.Status.Draft) && (
                    <div className="col-lg-12 mt-2 text-center">
                      <div className="text-center">
                        <hr />
                        <div className="mt-4 row d-flex flex-column align-items-center">
                          <div className="col-12 col-md-6 col-lg-4">
                            <SupportIdAlert requestId={requestId} />
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <Button disabled={updating} onClick={(e) => { handleUpdate(e, 'Draft'); }} className="block" size="large" variant="contained">Save As Draft</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }
              </>
            )
            : (
              <>
                <div className="col-sm-12 row" style={{ flexDirection: 'row' }}>
                  <div className="col-sm-12 col-md-3 col-lg-4" />
                  <div className="col-sm-12 col-md-6 col-lg-4" style={{ flexDirection: 'column' }}>
                    <Paper className="d-flex d-sm-block d-lg-block d-xl-block " style={{ minHeight: '30vw', backgroundColor: Colours.LighGrayBkgrd }}>
                      {getPreviewCom()}
                    </Paper>
                  </div>
                </div>

                <div className="col-12 row mt-5">
                  <div className="col-12 col-md-3 col-lg-4" />
                  <div className="col-12 col-md-6 col-lg-4 d-flex flex-column">
                    <SupportIdAlert requestId={requestId} />
                  </div>
                </div>

                <div className={`col-12 row ${!IsEmpty(requestId) ? 'mt-3' : 'mt-5'}`}>
                  <div className="col-12 col-md-3 col-lg-4" />

                  <div className="col-6 col-md-3 col-lg-2">
                    <div className="d-flex">
                      <Button
                        disabled={!!updating}
                        onClick={(e) => {
                          if (postStatus === 'Live') {
                            setOpenModal(true);
                          } else {
                            handleUpdate(e, 'Draft');
                          }
                        }}
                        style={{ position: 'relative', width: '100%', minHeight: '35px' }}
                        variant="contained"
                      >
                        Save As Draft
                      </Button>
                    </div>
                  </div>

                  <div className="col-6 col-md-3 col-lg-2">
                    <div className="d-flex">
                      <Button
                        disabled={updating}
                        onClick={(e) => { handleUpdate(e, 'Live'); }}
                        style={{ position: 'relative', width: '100%', minHeight: '35px' }}
                        variant="contained"
                        color={[Status.Live, Status.Pending].includes(postStatus) ? 'primary' : 'secondary'}
                      >
                        {[Status.Live, Status.Pending].includes(postStatus) ? 'Update' : 'Post to Live'}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )
        }
      </div>

      <Dialog
        fullWidth
        size="sm"
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Save to Draft</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <p>
              {'Please note on the following below upon updating the status back to '}
              <strong>Draft</strong>
              {', '}
              <u>post date</u>
              {' and '}
              <u>statuses</u>
              {' will be set to:'}
            </p>

            <ul style={{ color: Colours.Black }}>
              <li>
                Post date:
                {' '}
                <span style={{ color: Colours.Primary }}>---</span>
              </li>
              <li>
                Post status:
                {' '}
                <span style={{ color: Colours.Primary }}>Draft</span>
              </li>
              <li>
                Promo status:
                {' '}
                <span style={{ color: Colours.Primary }}>Live</span>
              </li>
              <li>
                Searchable status:
                {' '}
                <span style={{ color: Colours.Primary }}>Pending</span>
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => handleUpdate(e, 'Draft')} color="default" className="mr-4">Proceed</Button>
          <Button onClick={() => setOpenModal(false)} color="primary">Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

Post.propTypes = {
  match:  PropTypes.shape.isRequired,
  pageId: PropTypes.number.isRequired,

  getPage:       PropTypes.func.isRequired,
  getCategories: PropTypes.func.isRequired,
  onRedirect:    PropTypes.func.isRequired,
};

export default connect(null, { getCategories, getPage })(Post);
